import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import BookingFlowStepIndicator from '@/components/modules/booking-flow/booking-flow-step-indicator/booking-flow-step-indicator';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Button from '@/components/molecules/button/button';
import GenericAccordion from '@/components/organisms/generic-accordion/generic-accordion';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import BookingFlowModuleList, {
  BookingFlowModule,
} from '@/components/utilities/content-modules/booking-flow-module-list/booking-flow-module-list';
import { ModuleWrapperProps } from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useBookingFlowContext } from '@/libraries/contexts/booking-flow-context/booking-flow-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useSessionStorage, {
  SessionStorageKey,
} from '@/libraries/hooks/use-session-storage';
import {
  getFirstInvalidModule,
  isStepValid,
} from './booking-flow-utils/booking-flow-utils';
import styles from './booking-flow.module.scss';

export interface BookingFlowProps extends ModuleWrapperProps {
  /** Parameters from the current page */
  parameters?: Content.Components['BookingFlowModuleDto']['parameters'];
  /** The steps of the booking flow */
  steps?: Content.Components['BookingFlowModuleDto']['steps'];
}

const BookingFlow = ({ parameters, steps }: BookingFlowProps) => {
  const { t } = useI18n();
  const router = useRouter();
  const {
    isNavigationBlocked,
    activeBookingStep,
    moduleStatusMap,
    setActiveAnchor,
    setActiveBookingStep,
    setBookingFlowSteps,
  } = useBookingFlowContext();
  const searchParams = useSearchParams();

  const stepFromQuery = Number(searchParams!.get('activeStep') ?? 0);
  const initialActiveStep = steps
    ? Math.min(stepFromQuery, steps.length - 2) // prevent landing on last step
    : stepFromQuery;
  const initialMaxCompletedStep = searchParams!.get('maxCompletedStep')
    ? Number(searchParams!.get('maxCompletedStep'))
    : 0;

  const [returnUrl, setReturnUrl] = useSessionStorage<string>(
    SessionStorageKey.BOOKING_FLOW_RETURN_URL,
  );
  const [previousPageUrl] = useSessionStorage<string>(
    SessionStorageKey.PREVIOUS_PAGE_URL,
  );

  const [activeStep, setActiveStep] = useState(initialActiveStep);
  const [maxCompletedStep, setMaxCompletedStep] = useState(
    initialMaxCompletedStep,
  );
  const hasNextStep = steps && steps[activeStep + 1];
  const hasPreviousStep = steps && steps[activeStep - 1];

  const reset = () => {
    setActiveAnchor(null);
    setActiveBookingStep(null);
  };

  const canLeaveActiveStep = (): boolean => {
    if (!steps || isStepValid(steps[activeStep], moduleStatusMap)) {
      return true;
    }

    const moduleToScroll = getFirstInvalidModule(
      steps[activeStep],
      moduleStatusMap,
    );

    if (moduleToScroll) {
      setActiveAnchor(moduleToScroll.id);
    }

    return false;
  };

  const handleClickOnStep = (stepIndex: number) => {
    if (isNavigationBlocked) return;

    // If the user is trying to navigate forward
    if (stepIndex > activeStep) {
      // Check if leaving the current step is allowed
      if (!canLeaveActiveStep()) return;

      // Prevent jumping ahead beyond the next incomplete step
      if (stepIndex > maxCompletedStep + 1) return;

      // Ensure all steps before the target step are valid
      for (let index = 0; index <= stepIndex - 1; index++) {
        if (steps && !isStepValid(steps[index], moduleStatusMap)) {
          return;
        }
      }
    }

    setActiveStep(stepIndex);
    reset();
  };

  const handleClickNextStepButton = () => {
    if (isNavigationBlocked) return;

    if (!canLeaveActiveStep()) return;

    setActiveStep(previous => previous + 1);
    reset();

    if (!window) return;

    window.scrollTo({ behavior: 'smooth', top: 0 });
  };

  const handleClickPreviousStepLink = () => {
    if (isNavigationBlocked) return;

    setActiveStep(previous => previous - 1);
    reset();

    if (!window) return;

    window.scrollTo({ behavior: 'smooth', top: 0 });
  };

  const renderNextStepButton = () => {
    if (!hasNextStep) return null;

    return (
      <div className={styles.buttonWrapper}>
        <Button
          className={styles.button}
          onClick={handleClickNextStepButton}
          text={
            steps[activeStep].nextStepText &&
            steps[activeStep].nextStepText.trim() !== ''
              ? steps[activeStep].nextStepText
              : `${t('items.modules.bookingFlow.continueTo')} ${steps[activeStep + 1].summary.toLowerCase()}`
          }
          variant="primary"
        />
      </div>
    );
  };

  const renderPreviousStepLink = () => {
    if (activeStep === 0) {
      return (
        <AnchorLink
          icon="ArrowLeft"
          text={t('items.modules.bookingFlow.backToHotelPage')}
          url={returnUrl ?? undefined}
        />
      );
    }

    if (!hasPreviousStep) return null;

    return (
      <AnchorLink
        icon="ArrowLeft"
        onClick={handleClickPreviousStepLink}
        text={steps[activeStep - 1].summary}
      />
    );
  };

  useEffect(() => {
    if (!parameters?.bookingStateId) return;

    updateUrlWithParameters({
      newParameters: {
        activeStep: activeStep,
        bookingStateId: parameters.bookingStateId,
        maxCompletedStep: maxCompletedStep,
      },
      overwrite: true,
      router,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, maxCompletedStep, parameters?.bookingStateId]);

  useEffect(() => {
    setMaxCompletedStep(previous => {
      return Math.max(previous, activeStep);
    });
  }, [activeStep]);

  useEffect(() => {
    if (initialActiveStep !== 0) return;

    if (returnUrl !== null) return;

    if (previousPageUrl) {
      setReturnUrl(previousPageUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!steps) return;

    setBookingFlowSteps(steps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  useEffect(() => {
    if (activeBookingStep === null) return;

    if (!steps) return;

    setActiveStep(activeBookingStep);
  }, [activeBookingStep, steps]);

  return (
    <div className={styles.component}>
      <Container className={styles.container}>
        <Row>
          <Column offset={{ laptop: 1 }} width={{ laptop: 10 }}>
            {steps && (
              <Row className={styles.stepIndicatorWrapper}>
                <Column>
                  <BookingFlowStepIndicator
                    activeStepIndex={activeStep}
                    completedStepIndex={maxCompletedStep}
                    onClick={stepIndex => handleClickOnStep(stepIndex)}
                    steps={steps}
                  />
                </Column>
              </Row>
            )}
          </Column>
        </Row>

        <Row className={styles.stepsWrapper}>
          <Column
            className={styles.column}
            offset={{ laptop: 1 }}
            width={{ laptop: 7, tablet: 8 }}
          >
            {steps && steps[activeStep] && (
              <React.Fragment>
                <div className={styles.content}>
                  <div className={styles.stepHeader}>
                    {renderPreviousStepLink()}

                    {steps[activeStep].summary && (
                      <Typography
                        className={styles.headline}
                        tagStyle="headingLarge"
                      >
                        {steps[activeStep].summary}
                      </Typography>
                    )}
                  </div>

                  {steps[activeStep].sections.length > 0 && (
                    <div className={styles.sections}>
                      {steps[activeStep].sections.map(
                        (section, sectionIndex) => {
                          if (section.accordionView) {
                            return (
                              <GenericAccordion
                                key={sectionIndex}
                                additionalInfo={section.accordionText}
                                className={styles.section}
                                headline={section.headline}
                                headlineTagStyle="headingMedium"
                                initialExpanded={!section.foldAccordionOnLoad}
                                showBottomBorder
                              >
                                <div className={styles.sectionAccordionContent}>
                                  {section.description && (
                                    <Typography
                                      className={styles.description}
                                      tagStyle="bodyMedium"
                                    >
                                      {section.description}
                                    </Typography>
                                  )}

                                  <div
                                    className={classNames(styles.modules, {
                                      [styles.gridView]: section.gridView,
                                    })}
                                  >
                                    <BookingFlowModuleList
                                      key={sectionIndex}
                                      contentModules={
                                        section.contentModules as unknown as BookingFlowModule[]
                                      }
                                    />
                                  </div>
                                </div>
                              </GenericAccordion>
                            );
                          }

                          return (
                            <div key={sectionIndex} className={styles.section}>
                              {(section.headline || section.description) && (
                                <div className={styles.sectionHeader}>
                                  <Typography
                                    className={styles.headline}
                                    tagStyle="headingMedium"
                                  >
                                    {section.headline}
                                  </Typography>

                                  <Typography
                                    className={styles.description}
                                    tagStyle="bodyMedium"
                                  >
                                    {section.description}
                                  </Typography>
                                </div>
                              )}

                              <div
                                className={classNames(styles.modules, {
                                  [styles.gridView]: section.gridView,
                                })}
                              >
                                <BookingFlowModuleList
                                  key={sectionIndex}
                                  contentModules={
                                    section.contentModules as unknown as BookingFlowModule[]
                                  }
                                />
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}
                </div>

                {renderNextStepButton()}
              </React.Fragment>
            )}
          </Column>

          {steps && steps[activeStep] && (
            <Column width={{ laptop: 3, tablet: 4 }}>
              <div className={styles.summary}>
                <BookingFlowModuleList
                  contentModules={
                    steps[activeStep]
                      .contentModules as unknown as BookingFlowModule[]
                  }
                />
              </div>
            </Column>
          )}
        </Row>
      </Container>
    </div>
  );
};

BookingFlow.displayName = 'BookingFlow';

export default BookingFlow;
